import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Heading, Link, Paragraph } from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import { Routes } from '../../../../enums/Routes';
import { coverageCounts } from './hrOverviewView';
import classes from './Overview.module.css';

/** How many teams are surveyed often enough, as the law requires */
export const CoverageCard = ({
  overview,
}: {
  overview: HrWorkEnvironmentOverview;
}): ReactElement => {
  const { t } = useTranslation();
  const counts = coverageCounts(overview.teams);
  const total = overview.teams.length;
  return (
    <section
      aria-labelledby="coverage-heading"
      className={`${classes.card} ${classes.wide}`}
    >
      <div className={classes.heading}>
        <Heading level={2} data-size="sm" id="coverage-heading">
          {t('hrOverview.coverage.title', {
            ok: counts.find((item) => item.coverage === 'ok')?.count ?? 0,
            total,
          })}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('hrOverview.coverage.intro', {
            months: overview.setup.intervalMonths,
          })}
        </Paragraph>
      </div>
      <div className={classes.bar} aria-hidden="true">
        {counts
          .filter((item) => item.count > 0)
          .map((item) => (
            <span
              key={item.coverage}
              className={classes.segment}
              data-coverage={item.coverage}
              style={{ flex: item.count }}
            />
          ))}
      </div>
      <ul className={classes.legend}>
        {counts.map((item) => (
          <li key={item.coverage}>
            <span
              aria-hidden="true"
              className={classes.dot}
              data-coverage={item.coverage}
            />
            <span className="ds-paragraph" data-size="sm">
              {t(`hrOverview.coverage.statuses.${item.coverage}`)}
            </span>
            <span className="ds-label" data-size="sm">
              {item.count}
            </span>
          </li>
        ))}
      </ul>
      <Link asChild data-size="sm" className={classes.start}>
        <RouterLink to={Routes.HRWorkEnvironmentTeamsFullPath}>
          {t('hrOverview.coverage.all', { count: total })}
        </RouterLink>
      </Link>
    </section>
  );
};
